import { ActivePromotion, IPackage } from '../graphQL/commons'
import {
  ICartGroup,
  ICartProduct,
  ICatalogItem,
  IConditionAction,
  IConfig,
  IPackageProduct,
  IProduct,
} from '../interfaces'
import { getMultiPromotionUiDetails } from './catalog/getMultiPromotionUiDetails'
import { setPromotionUiDetailsCatalog } from './catalog/setPromotionUiDetailsCatalog'

export interface IQuantityInfo {
  quantity: number
  promotionalQty: number
  index?: number
  minQtyAllowed?: number
  inCartQuantity?: number
  package?: IPackage | null
  discounts?: number
  price?: number
}
/**
 * A map object that contains product quantity info.
 * @typedef {(Record<string,IQuantityInfo>)} QuantityMapObject
 */
type QuantityMapObject = Record<string, IQuantityInfo>

/**
 * Arguments for setProductsQuantity function
 * @typedef {ISetProductsQuantityArgs} SetProductsQuantityArgs
 */
export interface ISetProductsQuantityArgs {
  products?: IProduct[]
  flattedCartItems?: ICartProduct[]
  config?: IConfig
  activePromotions: ActivePromotion[]
  isSuggestedProduct?: boolean
  isSplitPromotionTagAllowed?: boolean
}

export interface ISetItemsQuantityArgs {
  categories: ICatalogItem[]
  flattedCartItems: ICartProduct[]
  config?: IConfig
  activePromotions: ActivePromotion[]
}

export interface ITransformCategoryProductArgs {
  config?: IConfig
  product: IProduct
  quantityInfo?: IQuantityInfo
  flattedCartItems: ICartProduct[]
  activePromotions: ActivePromotion[]
}

export interface ITransformProductArgs extends ITransformCategoryProductArgs {
  isSuggestedProduct: boolean
  isSplitPromotionTagAllowed: boolean
}

export const setPackagesValues = (
  sku: string,
  extraPackages: IPackageProduct[],
  items: ICartProduct[],
): IPackageProduct[] =>
  extraPackages.map(({ name: packageName, ...data }) => {
    const packageItems = items.filter(
      (item) => item.package?.name === packageName && item.sku === sku,
    )
    return {
      ...data,
      value: packageItems[0]?.quantity || 0,
      name: packageName,
    }
  })

/**
 * Function declaration to flat items from cart and groups
 *
 * @internal
 * @param items - The cart items
 * @param groups - The groups of items
 * @returns Flatted items array
 */
export const getFlattedCartItems = ({
  items = [],
  groups = [],
}: {
  items?: ICartProduct[]
  groups?: ICartGroup[]
}): {
  flattedCartItems: ICartProduct[]
} => {
  if (groups.length) {
    // flat groups int ICartProduct[] as items
    const groupsItemsFlatted: ICartProduct[] = groups.flatMap(
      (item) => item.items,
    )
    return { flattedCartItems: groupsItemsFlatted }
  }
  return { flattedCartItems: items }
}

const productCategoryTransformers: IConditionAction<
  ITransformCategoryProductArgs,
  IProduct
>[] = [
  {
    condition: ({ quantityInfo, product: { extraPackages } }) =>
      !!quantityInfo?.package && !!extraPackages,
    transformAction: ({ product, flattedCartItems }) => {
      const extraPackages = setPackagesValues(
        product.sku,
        product.extraPackages,
        flattedCartItems,
      )
      return {
        ...product,
        extraPackages,
      }
    },
  },
  {
    condition: ({ quantityInfo }) => !!quantityInfo,
    transformAction: ({ product, quantityInfo }) => {
      const {
        quantity,
        promotionalQty,
        minQtyAllowed = 0,
        inCartQuantity,
      } = quantityInfo!
      return {
        ...product,
        quantity,
        promotionalQty,
        minQtyAllowed,
        inCartQuantity,
      }
    },
  },
  {
    condition: ({ config, product: { promotions } }) =>
      (!config?.options?.splitPromotionTagCatalog && !!promotions?.length) ||
      Number(promotions?.length) === 1,
    transformAction: ({ product, config, activePromotions }) => {
      const promotion = setPromotionUiDetailsCatalog({
        product,
        config,
        activePromotions,
      })
      return {
        ...product,
        promotion,
      }
    },
  },
  {
    condition: ({ config, product: { promotions } }) =>
      !!config?.options?.splitPromotionTagCatalog &&
      Number(promotions?.length) > 1,
    transformAction: ({ product, config }) => {
      const promotionsDetails = getMultiPromotionUiDetails({
        product,
        config: config!,
        textFrom: 'label',
        secondaryTextFrom: 'limit',
        consolidateVolumePromotions: true,
      })
      return {
        ...product,
        promotionsDetails,
      }
    },
  },
]

const getQuantitiesMapObject = (
  flattedCartItems: ICartProduct[],
): QuantityMapObject => {
  // quantities object declaration to avoid iterate the array each time
  let quantitiesObj: QuantityMapObject = {}

  // iterate newItems array to get { ... { sku : { quantity, index } } } object
  // to avoid iterate cart/group items to get a quantity for specific product
  flattedCartItems.forEach((product: ICartProduct, index: number) => {
    const {
      quantity,
      promotionalQty,
      minQtyAllowed,
      inCartQuantity,
      discounts,
      price,
    } = product
    quantitiesObj = {
      ...quantitiesObj,
      [product.sku]: {
        quantity,
        promotionalQty,
        index,
        minQtyAllowed,
        inCartQuantity,
        package: product?.package || null,
        discounts,
        price,
      },
    }
  })

  return quantitiesObj
}

/**
 * Function declaration to match catalog and cart items to set quantity on each catalog item
 * because headless doesn't provide the quantity on catalog items.
 *
 * @internal
 * @param categories - The categories
 * @param flattedItems - The flatted cart items
 * @returns Categories updated with quantity based on cart/groups
 */
export const setItemsQuantity = ({
  categories = [],
  flattedCartItems = [],
  config,
  activePromotions,
}: ISetItemsQuantityArgs): {
  categories: ICatalogItem[]
} => {
  const quantitiesObj = getQuantitiesMapObject(flattedCartItems)

  // iterate categories object to set quantity to each product
  // based on cart/groups and set promotion ui details
  const newCategories = categories.map(
    (category: ICatalogItem): ICatalogItem => ({
      ...category,
      productsPrices:
        category.productsPrices?.map((product: IProduct): IProduct => {
          // get product quantity from quantitiesObj
          const quantityInfo: IQuantityInfo | undefined =
            quantitiesObj[product.sku]

          return productCategoryTransformers.reduce<IProduct>(
            (acc, { condition, transformAction }) => {
              const args = {
                product: acc,
                quantitiesObj,
                flattedCartItems,
                quantityInfo,
                activePromotions,
                config,
              }
              if (condition(args)) {
                return transformAction(args)
              }
              return acc
            },
            {
              ...product,
              quantity: 0,
              promotionalQty: 0,
              minQtyAllowed: 0,
              inCartQuantity: 0,
            },
          )
        }) || [],
    }),
  )

  return {
    categories: newCategories,
  }
}

const productTransformers: IConditionAction<ITransformProductArgs, IProduct>[] =
  [
    {
      condition: ({ isSuggestedProduct, product: { quantity } }) =>
        isSuggestedProduct && !!quantity,
      transformAction: ({ product }) => {
        return {
          ...product,
          prefilledQuantity: product.quantity,
        }
      },
    },
    {
      condition: ({ quantityInfo, product: { extraPackages } }) =>
        !!quantityInfo?.package && !!extraPackages,
      transformAction: ({ product, flattedCartItems }) => {
        const extraPackages = setPackagesValues(
          product.sku,
          product.extraPackages,
          flattedCartItems,
        )
        return {
          ...product,
          extraPackages,
        }
      },
    },
    {
      condition: ({ quantityInfo }) => !!quantityInfo,
      transformAction: ({ product, quantityInfo }) => {
        const {
          price = 0,
          discounts = 0,
          quantity = 0,
          promotionalQty = 0,
          minQtyAllowed = 0,
        } = quantityInfo!
        return {
          ...product,
          quantity,
          promotionalQty,
          minQtyAllowed,
          subtotal: quantity * price - discounts,
        }
      },
    },
    {
      condition: ({ quantityInfo }) => !quantityInfo,
      transformAction: ({ product }) => {
        return {
          ...product,
          quantity: 0,
          promotionalQty: 0,
          minQtyAllowed: 0,
          subtotal: 0,
        }
      },
    },
    {
      condition: ({ product: { promotions }, isSplitPromotionTagAllowed }) =>
        (!isSplitPromotionTagAllowed && !!promotions?.length) ||
        Number(promotions?.length) === 1,
      transformAction: ({ product, config, activePromotions }) => {
        const promotion = setPromotionUiDetailsCatalog({
          product,
          config,
          activePromotions,
        })
        return {
          ...product,
          promotion,
        }
      },
    },
    {
      condition: ({
        config,
        product: { promotions },
        isSplitPromotionTagAllowed,
      }) =>
        isSplitPromotionTagAllowed &&
        !!config?.options?.splitPromotionTagCatalog &&
        Number(promotions?.length) > 1,
      transformAction: ({ product, config }) => {
        const promotionsDetails = getMultiPromotionUiDetails({
          product,
          config: config!,
          textFrom: 'label',
          secondaryTextFrom: 'limit',
          consolidateVolumePromotions: true,
        })

        return {
          ...product,
          promotionsDetails,
        }
      },
    },
  ]

/**
 * Pass a products list and return the same products list but with the quantity of cart updated
 * @param products[]
 * @param flattedCartItems[]
 * @returns products[]
 */
export const setProductsQuantity = ({
  products = [],
  flattedCartItems = [],
  config,
  activePromotions,
  isSuggestedProduct = false,
  isSplitPromotionTagAllowed = false,
}: ISetProductsQuantityArgs): IProduct[] => {
  const quantitiesObj = getQuantitiesMapObject(flattedCartItems)
  return products.map((currentProduct) => {
    const quantityInfo: IQuantityInfo | undefined =
      quantitiesObj[currentProduct.sku]

    return productTransformers.reduce<IProduct>(
      (acc, { condition, transformAction }) => {
        const args = {
          product: acc,
          quantitiesObj,
          flattedCartItems,
          quantityInfo,
          activePromotions,
          config,
          isSuggestedProduct,
          isSplitPromotionTagAllowed,
        }
        if (condition(args)) {
          return transformAction(args)
        }
        return acc
      },
      currentProduct,
    )
  })
}
